import React from 'react';
import { logGlobalError } from '../utils/globalErrorLogger';

/**
 * App-root error boundary. React render-phase errors don't reach
 * window.onerror, so we need this to catch them and forward to Cloud
 * Logging / Error Reporting via `logGlobalError`.
 *
 * Production fallback is intentionally minimal — a reload usually clears
 * transient render failures. Debug builds rethrow so the dev gets the
 * actual stack instead of a friendly message.
 */

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

const IS_DEV = process.env.NODE_ENV !== 'production';

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    logGlobalError(error, {
      context: 'ReactErrorBoundary',
      metadata: { componentStack: info.componentStack ?? undefined },
      severity: 'ERROR',
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold text-slate-900">Something went wrong</h1>
          <p className="mt-2 text-slate-600">
            The page hit an unexpected error. Reloading usually clears it.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-6 inline-flex items-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Reload page
          </button>
          {IS_DEV && this.state.error ? (
            <pre className="mt-6 max-h-64 overflow-auto rounded bg-red-50 p-3 text-left text-xs text-red-900">
              {this.state.error.stack || this.state.error.message}
            </pre>
          ) : null}
        </div>
      </div>
    );
  }
}
